import React, { useEffect, useState, useRef } from 'react';
import api, { formatApiError, API_BASE } from '../api';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Upload, FileDown, Trash2, Folder, File } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_LABEL = {
    proposal: 'Proposal', signed_contract: 'Signed Contract', invoice: 'Invoice',
    statement: 'Statement', meeting_notes: 'Meeting Notes', discovery: 'Discovery', scope: 'Scope', other: 'Other',
};
const TYPES = Object.keys(TYPE_LABEL);

function humanSize(n) {
    if (!n && n !== 0) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function DocumentsGlobal() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState('all');
    const [clients, setClients] = useState([]);
    const [uploadOpen, setUploadOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (typeFilter !== 'all') params.type = typeFilter;
            const r = await api.get('/documents', { params });
            setItems(r.data.items);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter]);
    useEffect(() => { api.get('/clients', { params: { per_page: 200 } }).then(r => setClients(r.data.items)); }, []);

    const del = async (docId) => {
        if (!confirm('Delete this document?')) return;
        try { await api.delete(`/documents/${docId}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const clientById = Object.fromEntries(clients.map(c => [c.id, c.company_name]));

    return (
        <div data-testid="documents-page" className="bm-fade-in">
            <PageHeader
                title="Documents"
                description="Client documents — proposals, signed contracts, invoices, scope, meeting notes."
                actions={
                    <Button onClick={() => setUploadOpen(true)} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="documents-upload-btn">
                        <Upload size={16} className="mr-1" /> Upload
                    </Button>
                }
            >
                <div className="mt-5 flex items-center gap-3">
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                        <SelectTrigger className="h-9 w-[180px] bg-white" data-testid="documents-type-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            {TYPES.map(t => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </PageHeader>

            <div className="px-8 py-6">
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                    {loading && <div className="px-6 py-10 text-center text-[color:var(--bm-text-muted)]">Loading…</div>}
                    {!loading && items.length === 0 && (
                        <div className="px-6 py-14 text-center">
                            <Folder size={36} className="mx-auto text-[color:var(--bm-text-subtle)]" />
                            <div className="mt-3 text-[color:var(--bm-text-muted)]">No documents yet.</div>
                        </div>
                    )}
                    {!loading && items.length > 0 && (
                        <table className="w-full text-[13px]">
                            <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                                <tr>
                                    <th className="text-left font-medium px-5 py-3">Filename</th>
                                    <th className="text-left font-medium px-4 py-3">Type</th>
                                    <th className="text-left font-medium px-4 py-3">Client</th>
                                    <th className="text-right font-medium px-4 py-3">Size</th>
                                    <th className="text-left font-medium px-4 py-3">Uploaded</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(d => (
                                    <tr key={d.id} className="border-t border-[color:var(--bm-border)]" data-testid={`document-row-${d.id}`}>
                                        <td className="px-5 py-3.5"><File size={14} className="inline mr-1.5 text-[color:var(--bm-text-subtle)]" />{d.filename}</td>
                                        <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{TYPE_LABEL[d.type] || d.type}</td>
                                        <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{clientById[d.client_id] || '—'}</td>
                                        <td className="px-4 py-3.5 text-right text-[color:var(--bm-text-muted)]">{humanSize(d.size_bytes)}</td>
                                        <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{new Date(d.uploaded_at).toLocaleString('en-GB')}</td>
                                        <td className="px-4 py-3.5 text-right">
                                            <div className="inline-flex gap-1">
                                                <a href={`${API_BASE}/documents/${d.id}/download`} className="p-1.5 text-[color:var(--bm-text-subtle)] hover:text-[color:var(--bm-primary)]" title="Download"><FileDown size={15} /></a>
                                                <button onClick={() => del(d.id)} className="p-1.5 text-[color:var(--bm-text-subtle)] hover:text-red-600" title="Delete"><Trash2 size={15} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} clients={clients} onUploaded={() => { setUploadOpen(false); load(); }} />
        </div>
    );
}

export function UploadDialog({ open, onClose, clients, preClientId, onUploaded }) {
    const [clientId, setClientId] = useState(preClientId || '');
    const [docType, setDocType] = useState('other');
    const [notes, setNotes] = useState('');
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => { if (open) { setClientId(preClientId || ''); setDocType('other'); setNotes(''); } }, [open, preClientId]);

    const submit = async (e) => {
        e.preventDefault();
        const f = fileRef.current?.files?.[0];
        if (!f) return toast.error('Select a file');
        if (!clientId) return toast.error('Select a client');
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('client_id', clientId);
            fd.append('type', docType);
            if (notes) fd.append('notes', notes);
            fd.append('file', f);
            await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Uploaded');
            onUploaded();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Upload failed'); }
        finally { setUploading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-[480px]" data-testid="upload-dialog">
                <DialogHeader>
                    <DialogTitle>Upload document</DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">Max 25MB per file.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 mt-2">
                    {!preClientId && (
                        <div>
                            <Label className="text-[12.5px]">Client *</Label>
                            <Select value={clientId} onValueChange={setClientId}>
                                <SelectTrigger className="mt-1.5 h-10" data-testid="upload-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                                <SelectContent>
                                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <Label className="text-[12.5px]">Type</Label>
                        <Select value={docType} onValueChange={setDocType}>
                            <SelectTrigger className="mt-1.5 h-10" data-testid="upload-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {TYPES.map(t => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[12.5px]">File *</Label>
                        <input ref={fileRef} type="file" className="mt-1.5 w-full text-[13px]" data-testid="upload-file-input" />
                    </div>
                    <div>
                        <Label className="text-[12.5px]">Notes</Label>
                        <Input value={notes} onChange={e => setNotes(e.target.value)} className="mt-1.5 h-10" />
                    </div>
                    <DialogFooter className="pt-2">
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={uploading} style={{ background: 'var(--bm-primary)' }} data-testid="upload-submit-btn">{uploading ? 'Uploading…' : 'Upload'}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
